import { isNil, pluck } from 'ramda';
import { __ } from '@wordpress/i18n';
import { queryClient, queryKeys } from '../../lib/query-client';
import ProjectService from '../../service/project-service';
import PermissionsService from '../../service/permissions-service';
import displayNetworkError from '../../helpers/displayNetworkError';
import handleSuccessAndDisplay from '../../helpers/handleSuccessAndDisplay';
import handleErrorAndDisplay from '../../helpers/handleErrorAndDisplay';
import { storeSet } from '../index';

export const makeProjectActions = (set, get) => {
    const fetchProjectsImpl = async () => {
        set('projectsLoading', true);
        set('projectsError', null);

        try {
            const data = await queryClient.fetchQuery({
                queryKey: queryKeys.projects,
                queryFn: ProjectService.getProjectsAsync,
            });

            set('projects', data);
            set('projectsLoading', false);
        } catch (error) {
            set('projectsError', error);
            set('projectsLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const refetchProjectsImpl = async () => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
        await fetchProjectsImpl();
    };

    const fetchProjectImpl = async (id) => {
        set('currentProjectLoading', true);
        set('currentProjectError', null);

        try {
            const data = await queryClient.fetchQuery({
                queryKey: queryKeys.project(id),
                queryFn: () => ProjectService.getProjectAsync(id),
            });

            set('currentProject', data);
            set('currentProjectLoading', false);
        } catch (error) {
            set('currentProjectError', error);
            set('currentProjectLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const fetchProjectTeamImpl = async (id) => {
        set('teamLoading', true);
        set('teamError', null);

        try {
            const data = await queryClient.fetchQuery({
                queryKey: queryKeys.projectTeam(id),
                queryFn: () => ProjectService.getProjectTeamAsync(id),
            });

            set('team', data);
            set('teamLoading', false);
        } catch (error) {
            set('teamError', error);
            set('teamLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const refetchProjectTeamImpl = async (id) => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(id) });
        await fetchProjectTeamImpl(id);
    };

    return {
        fetchProjectsImpl,
        refetchProjectsImpl,
        fetchProjectImpl,
        fetchProjectTeamImpl,
        refetchProjectTeamImpl,

        fetchProjects: fetchProjectsImpl,
        refetchProjects: refetchProjectsImpl,
        fetchProject: fetchProjectImpl,
        fetchProjectTeam: fetchProjectTeamImpl,
        refetchProjectTeam: refetchProjectTeamImpl,

        createUpdateProject: async (formData) => {
            try {
                await ProjectService.createUpdateAsync(formData);

                await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
                await refetchProjectsImpl();
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        createInvitationsBulkForProject: async (projectId, formData) => {
            try {
                const results = await ProjectService.createProjectTeamInvitationsBulkAsync(projectId, formData);
                const errors = pluck('error', results).filter(s => !isNil(s));

                if (errors.length === 0) {
                    handleSuccessAndDisplay(__('Invitations sent!', 'soundbetween'));
                    storeSet('refetchProjectTeam', projectId);
                } else {
                    errors.map(s => handleErrorAndDisplay(s));
                }

                return results;
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        assignPermissions: async (projectId, queryParams) => {
            try {
                await PermissionsService.permissionsAssign(queryParams);

                handleSuccessAndDisplay(__('Permissions assigned successfully!', 'soundbetween'));

                await queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(projectId) });
                await queryClient.invalidateQueries({ queryKey: queryKeys.userMe });

                await refetchProjectTeamImpl(projectId);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        unassignPermissions: async (projectId, queryParams) => {
            try {
                await PermissionsService.permissionsUnAssign(queryParams);

                handleSuccessAndDisplay(__('Permissions removed successfully!', 'soundbetween'));

                await queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(projectId) });
                await queryClient.invalidateQueries({ queryKey: queryKeys.userMe });

                await refetchProjectTeamImpl(projectId);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },
    };
};
